/* eslint-disable max-len */
import { Form, Row, Table } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import { JinaOsobaPodpisEditBtn } from 'components/molecules/Dokumenty/JinaOsobaPodpisEditBtn';
import FormWrapper from 'components/molecules/FormWrapper';
import PhoneNumberInput from 'components/molecules/PhoneNumberInput';

import { IDokJinaOsoba, dokJinaOsoba, dokJinaOsobaPridat, dokJinaOsobyGet } from 'services/dokumenty/prikazy';

import { EStavPodpisu } from 'enums/dokumentyEnums';

import emailValidation from 'utils/validations/emailValidation';

interface IOsobyJina {
    nextStep: () => void;
    prevStep: () => void;
    zadostID: number;
}

const OsobyJina: React.FC<IOsobyJina> = ({ nextStep, prevStep, zadostID }) => {
    const [form] = Form.useForm();
    const { profily } = useContext(Context);
    const [osoby, setOsoby] = useState<IDokJinaOsoba[]>();
    const [loading, setLoading] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [renderEditBtn, setAllowEdit, , allowFunc] = JinaOsobaPodpisEditBtn(zadostID, setLoading, setRefetch);
    const hasOsobySigned = !!osoby?.filter(
        e => e.stavpodpisu === EStavPodpisu.PODEPSANO || e.stavpodpisu === EStavPodpisu.NEOVERENO
    ).length;

    useEffect(() => {
        setLoading(true);
        dokJinaOsobyGet(zadostID)
            .then(resp => {
                setOsoby(resp.osoby);
                setAllowEdit(false);
            })
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch]);

    const osobyCols = [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            render: (jmeno: string, row: IDokJinaOsoba) => `${jmeno} ${row.prijmeni}`
        },
        {
            title: <TranslateText>EMAIL</TranslateText>,
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: <TranslateText>TELEFON</TranslateText>,
            dataIndex: 'telefon',
            key: 'telefon'
        },
        {
            render: (_text: string, row: IDokJinaOsoba, index: number) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'right'
                    }}
                >
                    {row.uzivateloid !== Number(profily?.id) && (
                        <AnchorComponent
                            id={`OsobyOdebratBtn${index}`}
                            key={`OsobyOdebratBtn${index}`}
                            onClick={() => {
                                setLoading(true);
                                dokJinaOsoba(zadostID, row.id, 'DELETE')
                                    .then(() => setRefetch(pv => !pv))
                                    .finally(() => setLoading(false));
                            }}
                        >
                            <TranslateText>ODEBRAT</TranslateText>
                        </AnchorComponent>
                    )}
                </div>
            )
        }
    ];

    return !showAddForm ? (
        <LoadingWrapper loading={loading}>
            <Title level={5} text="JINA_OSOBA_ZASTUPUJE" dividerTitle={true} />
            <Table
                rowKey={(_, index) => index || 0}
                locale={{
                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                }}
                style={{
                    margin: '30px 0px'
                }}
                columns={osobyCols}
                dataSource={osoby}
                pagination={false}
                rowClassName={(_a, i) => {
                    const classes = [];
                    if (i % 2) classes.push('tableRowGray');
                    return classes.join(' ');
                }}
                footer={() => (
                    <AnchorComponent
                        id="pridatOsobuBtn"
                        style={{
                            textAlign: 'center',
                            display: 'block'
                        }}
                        onClick={() => {
                            setShowAddForm(true);
                            form.resetFields();
                        }}
                    >
                        <TranslateText>PRIDAT_OSOBU</TranslateText>
                    </AnchorComponent>
                )}
            />

            <Row className="button-row">
                <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} id="OsobyZpetBtn" type="default" />
                <ButtonComponent buttontext="POKRACOVAT" id="OsobyPokracovatBtn" type="primary" onClick={nextStep} />
            </Row>

            {hasOsobySigned && renderEditBtn()}
        </LoadingWrapper>
    ) : (
        <LoadingWrapper loading={loading}>
            <FormWrapper
                outForm={form}
                onFinish={vals => {
                    setLoading(true);
                    dokJinaOsobaPridat(zadostID, vals as IDokJinaOsoba)
                        .then(() => {
                            setRefetch(pv => !pv);
                            setShowAddForm(false);
                        })
                        .finally(() => setLoading(false));
                }}
            >
                <InputComponent label="JMENO" name="jmeno" />
                <InputComponent label="PRIJMENI" name="prijmeni" />
                <InputComponent label="EMAIL" name="email" type="email" rules={emailValidation()} />
                <PhoneNumberInput label="TELEFON" name="telefon" />
                <Row className="button-row">
                    <ButtonComponent
                        buttontext="ZPET"
                        onClick={() => setShowAddForm(false)}
                        id="OsobyZpetBtn"
                        type="default"
                    />
                    <ButtonComponent
                        buttontext="POKRACOVAT"
                        id="OsobyPokracovatBtn"
                        type="primary"
                        onClick={() => (hasOsobySigned ? allowFunc(form.submit) : form.submit())}
                    />
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default OsobyJina;
